/**
 * Discover — INVENTORY §1 screen 3 / §4.1; prototype template lines 97-171.
 *
 * The search bar is deliberately NOT `<SearchBox>`: the prototype inlines it so
 * it can carry the 2px accent border, the glow shadow and the ✕ clear
 * (INVENTORY §11). Everything else is a straight DOM + inline-style port.
 *
 * The `Image()` art probe this screen resolves its tiles with lives in
 * `components/useArt.ts` — it is shared with every other screen that paints a
 * capsule (P3c-01, closed in the integration pass).
 */

import type { CSSProperties } from 'react';

import { CoverArt } from '../components/CoverArt.tsx';
import { Ribbon } from '../components/Ribbon.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { NO_STEAM_TIP, quickAddBlocked, removeChip, SORT_OPTIONS, viewSeg } from '../store/selectors.3c.ts';
import { discoverVM } from '../store/selectors.ts';

// ---------------------------------------------------------------------------
// screen
// ---------------------------------------------------------------------------

const ADD_BLOCKED: { bd: string; clr: string; bg: string } = {
  bd: 'var(--gt-border-2)',
  clr: 'var(--gt-muted-2)',
  bg: 'rgba(5,5,8,.5)',
};

type Tile = ReturnType<typeof discoverVM>['results'][number];

function GalleryTile({ g }: { g: Tile }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const openAdd = useQd((s) => s.openAdd);
  const art = useArt(g.id + 'p', g.coverCands);
  const blocked = quickAddBlocked(g.id);
  const tone = blocked && !g.inList ? ADD_BLOCKED : { bd: g.addBd, clr: g.addClr, bg: g.addBg };

  return (
    <div
      onClick={() => openGame(g.id)}
      style={{
        position: 'relative',
        aspectRatio: '2/3',
        background: g.fall,
        overflow: 'hidden',
        border: '1px solid var(--gt-border)',
        cursor: 'pointer',
      }}
    >
      <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
      <Ribbon up={g.upcoming} ea={g.ea} demo={g.demo} />
      {g.hasDisc ? (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontFamily: 'var(--gt-font-mono)',
            fontWeight: 700,
            fontSize: 11,
            background: 'var(--acc)',
            color: 'var(--gt-ink)',
            padding: '3px 6px',
          }}
        >
          {g.disc}
        </div>
      ) : null}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg,transparent 38%,rgba(5,5,8,.97) 78%)',
        }}
      />
      <div style={{ position: 'absolute', inset: 0, background: g.sheen, mixBlendMode: 'screen', pointerEvents: 'none' }} />
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '11px 11px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2, color: '#fff' }}>{g.name}</div>
        <div style={{ fontSize: 10.5, color: 'var(--gt-text-4)' }}>{g.dev}</div>
        <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 9, color: 'var(--gt-text-5)', letterSpacing: '.03em' }}>
          {g.priceTxt} · {g.playersTxt} · {g.dateTxt}
        </div>
        {/* Why the feed chose this row. Server-authored and rendered verbatim —
            the client cannot recompute it without the taste profile and the
            ownership join. Empty on every non-feed row, so browse and the
            fallback are unchanged. */}
        {g.hasWhy ? (
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 8.5,
              color: 'var(--acc)',
              letterSpacing: '.04em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {g.whyTxt}
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 1 }}>
          <span
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 8.5,
              color: g.revClr,
              whiteSpace: 'nowrap',
              display: 'flex',
              gap: 4,
              minWidth: 0,
            }}
          >
            {/* The label truncates; the number never does — see `TileVM.revLbl`. */}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
              ▮ {g.revLbl}
            </span>
            {g.revPct ? <span style={{ flex: 'none' }}>{g.revPct}</span> : null}
          </span>
          <span
            data-tip={blocked && !g.inList ? NO_STEAM_TIP : g.addTip}
            onClick={(ev) => {
              ev.stopPropagation();
              if (blocked && !g.inList) return;
              openAdd(g.id);
            }}
            style={{
              width: 38,
              height: 38,
              border: '1px solid ' + tone.bd,
              color: tone.clr,
              background: tone.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingBottom: 2,
              boxSizing: 'border-box',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 17,
              fontWeight: 700,
              lineHeight: 1,
              flex: 'none',
              cursor: blocked && !g.inList ? 'not-allowed' : 'pointer',
            }}
          >
            {g.addG}
          </span>
        </div>
      </div>
    </div>
  );
}

function ListRow({ g }: { g: Tile }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const openAdd = useQd((s) => s.openAdd);
  const art = useArt(g.id + 'w', g.hdrCands);
  const blocked = quickAddBlocked(g.id);
  const tone = blocked && !g.inList ? ADD_BLOCKED : { bd: g.addBd, clr: g.addClr, bg: g.addBg };
  const ell: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  return (
    <div
      onClick={() => openGame(g.id)}
      style={{
        display: 'flex',
        gap: 11,
        alignItems: 'center',
        padding: '9px 10px',
        border: '1px solid var(--gt-line)',
        background: 'var(--gt-card)',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 96,
          aspectRatio: '460/215',
          background: g.fall,
          overflow: 'hidden',
          flex: 'none',
        }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={10} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gt-text)', ...ell }}>{g.name}</div>
        <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 9, color: 'var(--gt-text-5)', ...ell }}>
          {g.priceTxt} · {g.playersTxt} · {g.dateTxt}
        </div>
        <div
          style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 8.5, color: g.revClr, display: 'flex', gap: 4, minWidth: 0 }}
        >
          <span style={{ ...ell }}>▮ {g.revLbl}</span>
          {g.revPct ? <span style={{ flex: 'none' }}>{g.revPct}</span> : null}
        </div>
      </div>
      <span
        data-tip={blocked && !g.inList ? NO_STEAM_TIP : g.addTip}
        onClick={(ev) => {
          ev.stopPropagation();
          if (blocked && !g.inList) return;
          openAdd(g.id);
        }}
        style={{
          width: 44,
          height: 44,
          border: '1px solid ' + tone.bd,
          color: tone.clr,
          background: tone.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingBottom: 2,
          boxSizing: 'border-box',
          fontFamily: 'var(--gt-font-mono)',
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1,
          flex: 'none',
          cursor: blocked && !g.inList ? 'not-allowed' : 'pointer',
        }}
      >
        {g.addG}
      </span>
    </div>
  );
}

export default function Discover(): JSX.Element {
  const s = useQd();
  const v = discoverVM(s);
  const seg = viewSeg(s.view);

  // §5.3: one batched Steam art lookup per screenful, coalesced 500 ms.
  //
  // EVERY LOADED ROW, NOT THE FIRST `GI_MAX_BATCH`. This used to slice to 50,
  // and the argument for it was: five LOAD MOREs is 300 rows, so an uncapped
  // pass spends six items requests on rows nobody can see, while "the rest
  // keep whatever the page fetch gave them, which for a feed row is
  // `discover_page`'s server-side join and not a gap".
  //
  // THAT SENTENCE NAMES ITS OWN EXCEPTION. It is true of a feed row and false
  // of a browse row: `toBrowseGame` hard-codes `p: null, o: 0, rd: ''`
  // because a page cached for ten minutes has no business quoting a price, so
  // for a tag or term search the items refresh is not a top-up, it is the
  // ONLY source those three fields have. Capping it left every row past the
  // fiftieth permanently reading TBA — reproduced against production with
  // five tag hits (Terra Invicta, WARNO, Broken Arrow, Fallout Tactics,
  // Modern Warfare), all five priced by `POST /api/steam/items` when asked
  // and none of them ever asked about. A tag browse pages at exactly
  // `BROWSE_PAGE`, so the first LOAD MORE is already past the cap; a term
  // browse also PREPENDS its local matches, which spend the budget before
  // Steam's rows — the thin ones — reach it.
  //
  // Uncapped is not unbounded, because the floor this cap was approximating
  // already exists one layer down and is strictly better placed:
  // `revalidateSurface` stamps `revalAt` PER APPID, so a LOAD MORE re-passes
  // 300 ids and only the 50 new ones are due. One items request per page the
  // user explicitly asked for is the floor, not an excess — and the tail past
  // `GI_MAX_BATCH` goes out through the coalescing drain rather than at once.
  // The live-count overlay is unaffected: `runCcu` takes its own 12-slot
  // budget off the head of whatever is due.
  useQueueArt(v.results.map((g) => g.id));

  return (
    <div data-screen-label="Discover" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: '16px 14px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            border: '2px solid var(--acc)',
            background: 'var(--gt-card)',
            padding: '12px 13px',
            boxShadow: '0 0 0 4px rgba(0,0,0,.55),0 0 30px var(--accSoft)',
          }}
        >
          <span style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 15, color: 'var(--acc)' }}>▸</span>
          <input
            value={v.q}
            onChange={(e) => s.setQ(e.target.value)}
            placeholder="SEARCH GAMES, TAGS…"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 0,
              outline: 'none',
              color: 'var(--gt-text)',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 14,
              padding: 0,
            }}
          />
          {v.hasQ ? (
            <span
              onClick={() => s.clearQ()}
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: 14,
                color: 'var(--gt-muted)',
                cursor: 'pointer',
                padding: 4,
              }}
            >
              ✕
            </span>
          ) : null}
        </div>
        <div
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 9.5,
            letterSpacing: '.06em',
            color: 'var(--gt-muted)',
            textAlign: 'center',
          }}
        >
          {v.matchLine}
        </div>
        {/*
          WHY THE FEED IS AS PERSONAL AS IT IS. Second line, under the count,
          because it qualifies the count rather than replacing it.

          The fallback wording is the load-bearing one: DISCOVERY.md §5.6 says a
          silent fallback is how a broken Phase 2 would look identical to a
          working one. Without this line a member whose RPC failed sees a
          plausible grid of local games and no way to know the personalisation
          stopped. Accented in that state so it reads as a condition rather than
          a caption.
        */}
        {v.hasFeedTxt ? (
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 9,
              letterSpacing: '.06em',
              color: v.isFallback ? 'var(--acc)' : 'var(--gt-muted-2)',
              textAlign: 'center',
              marginTop: 5,
            }}
          >
            {v.feedTxt}
          </div>
        ) : null}
        {/*
          The same rule one mode over: a control that is set but cannot be
          honoured says so. In browse mode Steam owns the sort and the
          narrowing, and its search has no player-count sort, no price floor and
          no player-count filter — so PEAK PLAYERS, the low end of the price
          range and the crew lens each name themselves here rather than sitting
          on screen looking applied. Accented, because it describes a control
          the reader has actively set and can act on.
        */}
        {v.hasGapTxt ? (
          <div
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 9,
              letterSpacing: '.06em',
              color: 'var(--acc)',
              textAlign: 'center',
              marginTop: 5,
            }}
          >
            {v.gapTxt}
          </div>
        ) : null}
      </div>

      {/*
        The `# TAGS` trigger and its own chip rail are gone: the picker is the
        last section of the filter sheet now, so the tags ride the filter chip
        rail below and are counted in `+ FILTERS · N`. One control, one count.
      */}
      <div style={{ display: 'flex', gap: 8, padding: '12px 14px 0' }}>
        <div
          onClick={() => s.toggleFltOpen()}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid ' + v.fltBtnBd,
            color: v.fltBtnClr,
            background: v.fltBtnBg,
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 10.5,
            height: 44,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
          }}
        >
          {v.fltBtnTxt}
        </div>
        <div style={{ position: 'relative', flex: 1 }}>
          <select
            value={v.sortVal}
            onChange={(e) => s.setSort(e.target.value as typeof v.sortVal)}
            style={{
              width: '100%',
              height: 44,
              background: 'var(--gt-field)',
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text-3)',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 10,
              padding: '0 24px 0 10px',
              outline: 'none',
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>
                {o.t}
              </option>
            ))}
          </select>
          <span
            style={{
              position: 'absolute',
              right: 9,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--gt-muted)',
              fontSize: 9,
              pointerEvents: 'none',
            }}
          >
            ▾
          </span>
        </div>
        <div style={{ display: 'flex', flex: 'none' }}>
          <div
            onClick={() => s.setView('g')}
            style={{
              width: 44,
              height: 44,
              border: '1px solid ' + seg.g.bd,
              color: seg.g.clr,
              background: seg.g.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 15,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            ▦
          </div>
          <div
            onClick={() => s.setView('l')}
            style={{
              width: 44,
              height: 44,
              // Longhands, not `border` + `borderLeft:0` — same pixels, but the
              // shorthand/longhand mix makes React warn on every toggle, because
              // `seg.l.bd` changes on re-render. Same call as P3d's D-4 on the
              // Interests ≡ LIST segment.
              borderTop: '1px solid ' + seg.l.bd,
              borderRight: '1px solid ' + seg.l.bd,
              borderBottom: '1px solid ' + seg.l.bd,
              borderLeft: 0,
              color: seg.l.clr,
              background: seg.l.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 15,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            ≡
          </div>
        </div>
      </div>

      {v.fltN > 0 ? (
        <div
          data-hs="1"
          style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '10px 14px 0', overflowX: 'auto' }}
        >
          {v.fltChips.map((c) => (
            <div
              key={c.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                border: '1px solid ' + c.bd,
                color: c.clr,
                background: c.bg,
                padding: '9px 11px',
                whiteSpace: 'nowrap',
                fontFamily: 'var(--gt-font-mono)',
                fontSize: 10,
                flex: 'none',
              }}
            >
              {c.l}
              <span
                onClick={() => removeChip(useQd.getState(), c)}
                style={{ cursor: 'pointer', color: 'var(--gt-text-5)', padding: 2 }}
              >
                ✕
              </span>
            </div>
          ))}
          <div
            onClick={() => s.clearFlt()}
            style={{
              color: 'var(--gt-faint)',
              cursor: 'pointer',
              padding: '9px 6px',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 10,
              whiteSpace: 'nowrap',
              flex: 'none',
            }}
          >
            CLEAR ALL
          </div>
        </div>
      ) : null}

      {v.isGallery ? (
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, padding: '14px 14px 26px' }}
        >
          {v.results.map((g) => (
            <GalleryTile key={g.key} g={g} />
          ))}
        </div>
      ) : null}

      {v.isList ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 14px 26px' }}>
          {v.results.map((g) => (
            <ListRow key={g.key} g={g} />
          ))}
        </div>
      ) : null}

      {v.hasMore ? (
        <div style={{ padding: '0 14px 26px' }}>
          <div
            onClick={() => (v.isBrowse ? void s.fetchBrowse({ more: true }) : s.loadMoreDiscover())}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text-3)',
              background: 'var(--gt-field)',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: 10.5,
              height: 46,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {v.moreTxt}
          </div>
        </div>
      ) : null}

      {v.cappedTxt ? (
        <div
          style={{
            padding: '0 14px 26px',
            textAlign: 'center',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 9.5,
            letterSpacing: '.06em',
            color: 'var(--gt-muted)',
          }}
        >
          {v.cappedTxt}
        </div>
      ) : null}

      {v.noResults ? (
        <div
          style={{
            padding: '50px 20px',
            textAlign: 'center',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 11,
            color: 'var(--gt-muted)',
            lineHeight: 2,
          }}
        >
          {v.emptyTxt}
        </div>
      ) : null}

      {/*
        Decision 5: a filter that removed matches has to say so. Without this the
        thin rows a live search just fetched vanish with no explanation — the
        exact failure that made searching for an uncatalogued game hopeless
        whenever a filter was left on (docs/DISCOVERY.md §1.4 defect 1).
      */}
      {v.hasHidden ? (
        <div
          style={{
            padding: v.noResults ? '0 20px 40px' : '0 14px 26px',
            textAlign: 'center',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: 9.5,
            letterSpacing: '.06em',
            color: 'var(--acc2)',
          }}
        >
          {v.hiddenTxt}
        </div>
      ) : null}
    </div>
  );
}
